import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Clock,
  Search,
  Eye,
  Download,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Loader2,
  CircleX,
  GitCompareArrows,
  AlertCircle,
  Trash2,
  X,
  FileText,
} from "lucide-react";
import { getHistory, getRevisionComparison, getRevisionReport, comparePolicyDocuments, downloadPolicyDocument, deleteAsset } from "./services/dashboardService";
import { downloadReportPdf } from "../../utility/downloadReportPdf";
import { formatPolicyDate } from "../../utility/formatPolicyDate";
import ReportPrintView from "./components/ReportPrintView";
import { SourcePolicyCard } from "./components/PolicyResultModal";
import {
  getVendorComparisonOptions,
  compareSnapshots,
  openAssetSnapshotPdf,
} from "./services/dashboardService";

const PAGE_LIMIT = 20;
const TABS = ["Completed", "Ongoing", "Failed"];

// Tab → API analysis_status value
const TAB_STATUS = {
  Completed: "complete",
  Ongoing:   "processing",
  Failed:    "failed",
};

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${date} · ${time}`;
};

// Human-readable run time from milliseconds: "—", "52s", or "1m 3s".
const formatDuration = (ms) => {
  if (ms == null || Number.isNaN(Number(ms))) return "—";
  const n = Number(ms);
  // A sub-second run is real; showing "0s" reads as missing data rather than fast.
  if (n < 1000) return "<1s";
  const totalSec = Math.round(n / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
};

// Normalize a policy-document metadata object from the API into the UI shape.
const toDoc = (d) => (d ? {
  id:       d.id,
  version:  d.version,
  fileName: d.file_name,
  mimeType: d.mime_type,
} : null);

// Map one history API item → row shape used by tables/modals
const toRow = (item) => {
  // The document this analysis ran on rides under revision.documents[0]
  // (lists) or item.document (report responses).
  const doc = toDoc(item.revision?.documents?.[0] ?? item.document ?? null);
  return {
    id:           item.id,
    assetId:      item.asset_id ?? item.asset?.id ?? null,
    revisionId:   item.revision_id ?? item.revision?.id ?? null,
    company:      item.software_name ?? "—",
    vendorId:     item.asset?.vendor_id ?? item.asset?.vendor?.id ?? null,
    vendorName:   item.asset?.vendor?.name ?? item.software_name ?? null,
    vendorDomain: item.asset?.vendor?.primary_domain ?? null,
    // Which document this is for its vendor: a company can have a current
    // policy, a forthcoming one it has already published, and regional variants.
    policyRole:   item.asset?.policy_role ?? null,
    date:         formatDateTime(item.created_at),
    status:       item.asset?.analysis_status ?? "complete",
    overallScore: item.overall_score ?? null,
    privacyUrl:   item.privacy_url   ?? "",
    version:      item.version       ?? 1,
    policyChanged: false,
    document:     doc,
    // The PDF we generated of the vendor's own page. Distinct from `document`,
    // which is a file the USER uploaded — a URL-only analysis has no upload, and
    // a cache hit has no revision either, which is why both columns were blank.
    sourceSnapshot: item.source_snapshot ?? null,
    // A cache hit reuses an existing analysis, so there is no run to time.
    cached:       !item.revision_id && !item.revision,
    runtimeMs:    item.revision?.duration_ms ?? null,
    versions: [{
      key:        `v${item.version ?? 1}`,
      label:      `V${item.version ?? 1}`,
      latest:     item.revision?.is_latest ?? true,
      date:       formatDateTime(item.revision?.created_at || item.created_at),
      revisionId: item.revision_id ?? item.revision?.id ?? null,
      document:   doc,
      runtimeMs:  item.revision?.duration_ms ?? null,
    }],
  };
};

const normalizePrivacyAnalysis = (reportJson) => {
  const pa = reportJson?.privacy_analysis || reportJson?.privacyAnalysis || {};
  return pa && typeof pa === "object" ? pa : {};
};

const categoryDisplayName = (name) =>
  String(name || "")
    .replace(/_/g, " ")
    .replace(/\band\b/gi, "&")
    .replace(/\b\w/g, (m) => m.toUpperCase());

const getPrivacyCategoryList = (reportJson) => {
  const pa = normalizePrivacyAnalysis(reportJson);
  return Object.entries(pa).map(([name, cat]) => ({
    name,
    label: categoryDisplayName(name),
    score: Number(cat?.score ?? 0),
    confidence_score: Number(cat?.confidence_score ?? 0),
    summary: String(
      cat?.summary ?? cat?.category_summary ?? cat?.description ?? cat?.text ?? ""
    ).trim(),
  }));
};

const computeOverallFromCategories = (categories) => {
  if (!Array.isArray(categories) || categories.length === 0) return null;
  const avg = categories.reduce((acc, c) => acc + (Number.isFinite(c.score) ? c.score : 0), 0) / categories.length;
  return Number(avg.toFixed(1));
};

const computeCategoryDeltas = (fromCategories, toCategories) => {
  const fromMap = new Map(fromCategories.map((c) => [c.name, c]));
  const toMap = new Map(toCategories.map((c) => [c.name, c]));
  const names = new Set([...fromMap.keys(), ...toMap.keys()]);

  const deltas = [...names].map((name) => {
    const from = fromMap.get(name);
    const to = toMap.get(name);
    const fromScore = from ? Number(from.score || 0) : null;
    const toScore = to ? Number(to.score || 0) : null;
    const delta = fromScore !== null && toScore !== null ? toScore - fromScore : null;

    return {
      name,
      label: to?.label || from?.label || categoryDisplayName(name),
      fromScore,
      toScore,
      delta,
      fromCategory: from || null,
      toCategory: to || null,
    };
  });

  deltas.sort((a, b) => Math.abs(b.delta ?? 0) - Math.abs(a.delta ?? 0));
  return deltas;
};

const extractComparePayload = (response) => {
  const root = response?.data ?? response ?? {};
  const fromJson = root?.from || root?.from_report_json || root?.fromReportJson || root?.from_report || null;
  const toJson = root?.to || root?.to_report_json || root?.toReportJson || root?.to_report || null;
  return {
    fromJson: fromJson?.report_json ?? fromJson,
    toJson: toJson?.report_json ?? toJson,
  };
};

const extractExecutiveSummary = (reportJson) =>
  reportJson?.executive_summary || reportJson?.executiveSummary || reportJson?.executive || "";

const HistCategoryCard = ({ title, text }) => (
  <div className="bg-white border border-surface-border rounded-xl p-4">
    <p className="text-sm font-semibold text-primary mb-2">{title}</p>
    <p className="text-xs text-brand-muted leading-relaxed">{text}</p>
  </div>
);

const Overlay = ({ children }) => (
  <div className="fixed inset-0 z-50 bg-black/35 flex items-center justify-center p-4">{children}</div>
);

const BlueConfirmCard = ({ title, subtitle, leftLabel, rightLabel, onLeft, onRight }) => (
  <div className="w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl">
    <div className="bg-gradient-to-br from-[#3563E9] to-[#1E4CCF] text-white px-5 py-5">
      <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center mb-4">
        <AlertCircle className="w-4 h-4" />
      </div>
      <p className="text-2xl font-bold mb-1">{title}</p>
      <p className="text-sm text-white/80">{subtitle}</p>
      <div className="grid grid-cols-2 gap-2 mt-5">
        <button
          onClick={onLeft}
          className="h-10 rounded-xl border border-white/70 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
        >
          {leftLabel}
        </button>
        <button
          onClick={onRight}
          className="h-10 rounded-xl bg-white text-primary text-sm font-semibold hover:bg-slate-100 transition-colors"
        >
          {rightLabel}
        </button>
      </div>
    </div>
  </div>
);

const ThankYouModal = ({ isOpen, onClose, message }) => {
  if (!isOpen) return null;
  return (
    <Overlay>
      <div className="w-full max-w-sm bg-white rounded-2xl border border-surface-border p-6 text-center shadow-2xl">
        <div className="w-11 h-11 mx-auto rounded-full bg-success/10 text-success flex items-center justify-center mb-3">
          <CheckCircle2 className="w-6 h-6" />
        </div>
        <p className="text-lg font-bold text-brand-text">Thank you</p>
        <p className="text-sm text-brand-muted mt-1">{message}</p>
        <button
          onClick={onClose}
          className="mt-5 w-full h-10 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors"
        >
          Close
        </button>
      </div>
    </Overlay>
  );
};

const CancelAnalysisModal = ({ row, onBack, onConfirm }) => {
  if (!row) return null;
  return (
    <Overlay>
      <BlueConfirmCard
        title="Are you want to Cancel Analysis"
        subtitle="Cancelling the ongoing analysis will consume your vendor credit"
        leftLabel="Back"
        rightLabel="Cancel Analysis"
        onLeft={onBack}
        onRight={onConfirm}
      />
    </Overlay>
  );
};

const ReAnalyzeConfirmModal = ({ row, onClose, onConfirm }) => {
  if (!row) return null;
  return (
    <Overlay>
      <BlueConfirmCard
        title="Are you want to re-analysis"
        subtitle="Re-Analysis will consume analysis for your plan."
        leftLabel="Cancel"
        rightLabel="Re-Analysis"
        onLeft={onClose}
        onRight={onConfirm}
      />
    </Overlay>
  );
};

const DeleteConfirmModal = ({ row, busy, onClose, onConfirm }) => {
  if (!row) return null;
  return (
    <Overlay>
      <BlueConfirmCard
        title="Delete this analysis?"
        subtitle={`"${row.company}" and all its versions, reports, and uploaded documents will be removed from your history.`}
        leftLabel="Cancel"
        rightLabel={busy ? "Deleting…" : "Delete"}
        onLeft={onClose}
        onRight={onConfirm}
      />
    </Overlay>
  );
};

const CompareSelectModal = ({ row, onClose, onCompare, loading, error }) => {
  const [left, setLeft] = useState("");
  const [right, setRight] = useState("");
  if (!row) return null;

  return (
    <Overlay>
      <div className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl border border-surface-border bg-white">
        <div className="bg-gradient-to-br from-[#3563E9] to-[#1E4CCF] text-white px-6 py-4 text-center">
          <p className="text-2xl font-bold">{row.company.replace(" Inc.", "")}</p>
          <p className="text-xs text-white/80 mt-1">Compare Analysis</p>
        </div>

        <div className="p-5">
          <p className="text-xs text-brand-muted text-center mb-3">Please choose which analysis you want to compare</p>
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center mb-4">
            <div>
              <p className="text-[11px] text-brand-muted mb-1">Select Version</p>
              <select
                value={left}
                onChange={(e) => setLeft(e.target.value)}
                disabled={loading}
                className="w-full h-9 rounded-lg border border-surface-border px-3 text-xs"
              >
                <option value="">Select</option>
                {row.versions.map((v) => (
                  <option key={v.key} value={v.key}>
                    {v.label}{v.document ? ` — ${v.document.fileName}` : " — no file uploaded"}
                  </option>
                ))}
              </select>
            </div>
            <span className="text-xs text-brand-muted pt-5">VS</span>
            <div>
              <p className="text-[11px] text-brand-muted mb-1">Select Version</p>
              <select
                value={right}
                onChange={(e) => setRight(e.target.value)}
                disabled={loading}
                className="w-full h-9 rounded-lg border border-surface-border px-3 text-xs"
              >
                <option value="">Select</option>
                {row.versions.map((v) => (
                  <option key={v.key} value={v.key}>
                    {v.label}{v.document ? ` — ${v.document.fileName}` : " — no file uploaded"}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error && (
            <p className="text-xs text-danger mb-3">{error}</p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="h-9 rounded-lg border border-surface-border text-xs text-brand-muted disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => onCompare(left, right)}
              disabled={loading || !left || !right || left === right}
              className="h-9 rounded-lg bg-primary text-white text-xs font-semibold disabled:opacity-40 inline-flex items-center justify-center gap-1"
            >
              {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {loading ? "Comparing..." : "Compare Analysis"}
            </button>
          </div>
        </div>
      </div>
    </Overlay>
  );
};

const histBadgeClass = (verdict) => {
  const v = (verdict || "").toLowerCase();
  if (["good", "strong", "excellent"].includes(v)) return "bg-success/10 text-success";
  if (["fair", "moderate", "neutral"].includes(v))  return "bg-warning/10 text-warning";
  return "bg-danger/10 text-danger";
};

const HistCatCard = ({ cat }) => {
  const [open, setOpen] = useState(false);
  const pct = Math.round((cat.score / cat.maxScore) * 100);
  return (
    <div className="border border-surface-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-brand-text">{cat.name}</h4>
        <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${histBadgeClass(cat.verdict)}`}>
          {cat.verdict}
        </span>
      </div>
      <div className="flex items-center gap-3 mb-1">
        <div className="flex-1 h-2 bg-surface-border rounded-full overflow-hidden">
          <div className="h-full rounded-full bg-success transition-all" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs text-brand-muted w-14 text-right">{cat.score}/{cat.maxScore}</span>
      </div>
      <div className="flex items-center gap-3 mb-3">
        <div className="flex-1 h-2 bg-surface-border rounded-full overflow-hidden">
          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${cat.confidence}%` }} />
        </div>
        <span className="text-xs text-brand-muted w-14 text-right">{cat.confidence}%</span>
      </div>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs text-brand-muted hover:text-primary transition-colors"
      >
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {open ? "Hide detail" : "Show detail"}
      </button>
      {open && <p className="mt-2 text-sm text-brand-muted leading-relaxed">{cat.description}</p>}
    </div>
  );
};

// Display label + on-screen tone color + preferred order for known categories.
// This is a presentation HINT, not a whitelist: every category present in the
// server's privacy_analysis is rendered. Known keys keep their label/tone/
// position; any unknown/new key the server returns is appended in server order
// using its raw key as the label and a neutral tone (see orderedCategoryMeta).
const CATEGORY_META = [
  { key: "Data Usage",                  label: "Data Usage",                  tone: "text-warning"     },
  { key: "Data Sharing",                label: "Data Sharing",                tone: "text-primary"     },
  { key: "Security Commitments",        label: "Security Commitments",        tone: "text-orange-500"  },
  { key: "Confidentiality Protections", label: "Confidentiality Protections", tone: "text-success"     },
  { key: "Data Retention and Deletion", label: "Data Retention & Deletion",   tone: "text-danger"      },
  { key: "AI Training & Model Rights",  label: "AI Training & Model Rights",  tone: "text-primary"     },
];
const CATEGORY_META_BY_KEY = CATEGORY_META.reduce((m, c) => { m[c.key] = c; return m; }, {});
const CATEGORY_ORDER = CATEGORY_META.map((c) => c.key);

// Ordered, COMPLETE list of category-meta objects for whatever the server
// returned in `privacyAnalysis`. Known keys sort to their preferred position
// with their label/tone; unknown keys keep server order at the end (Array.sort
// is stable) with the raw key as label and a neutral tone.
const orderedCategoryMeta = (privacyAnalysis = {}) =>
  Object.keys(privacyAnalysis)
    .sort((a, b) => {
      const ia = CATEGORY_ORDER.indexOf(a);
      const ib = CATEGORY_ORDER.indexOf(b);
      if (ia === -1 && ib === -1) return 0;
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    })
    .map((key) => CATEGORY_META_BY_KEY[key] ?? { key, label: key, tone: "text-brand-text" });

const scoreToOffset = (score, max = 10) => {
  // Circle circumference ≈ 251. Full = 0 offset, empty = 251.
  const pct = Math.min(Math.max(score ?? 0, 0), max) / max;
  return Math.round(251 - pct * 251);
};

const HistoryResultsModal = ({ row, specificVersion, onClose }) => {
  const [tab, setTab] = useState("summary");
  const [activeVersion, setActiveVersion] = useState(
    specificVersion?.key ?? row?.versions?.[0]?.key ?? "v1"
  );
  const [reportData,    setReportData]    = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError,   setReportError]   = useState("");
  const [pdfLoading,    setPdfLoading]    = useState(false);
  const printRef = useRef(null);

  // Resolve the revisionId for the currently active version
  const activeRevisionId = (() => {
    if (specificVersion) return specificVersion.revisionId;
    const ver = row?.versions?.find((v) => v.key === activeVersion);
    return ver?.revisionId ?? row?.revisionId ?? null;
  })();

  useEffect(() => {
    if (!activeRevisionId) return;
    let cancelled = false;
    setReportLoading(true);
    setReportError("");
    setReportData(null);
    getRevisionReport(activeRevisionId)
      .then((res) => {
        if (!cancelled) setReportData(res?.data ?? res ?? null);
      })
      .catch((err) => {
        if (!cancelled) setReportError(err?.message || "Failed to load report.");
      })
      .finally(() => {
        if (!cancelled) setReportLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeRevisionId]);

  if (!row) return null;

  const showSidebar = !specificVersion && (row.versions?.length ?? 0) >= 1;

  // Derive display values from actual API shape:
  // { data: { asset: { software_name, ... }, report: { created_at, report_json: { privacy_analysis, executive_summary, website_summary, ... } } } }
  const asset        = reportData?.asset ?? null;
  const report       = reportData?.report ?? null;
  const reportJson   = report?.report_json ?? null;
  const categories   = reportJson?.privacy_analysis ?? {};

  // Compute overall score as the average across every returned category
  // (API has no top-level overall_score).
  const categoryScores = orderedCategoryMeta(categories).map(({ key }) => categories[key]?.score).filter((s) => s != null);
  const overallScore   = categoryScores.length
    ? +(categoryScores.reduce((a, b) => a + b, 0) / categoryScores.length).toFixed(1)
    : (row.overallScore ?? 0);

  const toolSummary  = reportJson?.website_summary   ?? "";
  const execSummary  = reportJson?.executive_summary ?? "";
  const analyzedDate = report?.created_at
    ? new Date(report.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : row.date;
  const policyAsOf    = formatPolicyDate(report?.policy_date_raw ?? reportJson?.policy_date ?? null);
  const softwareName  = asset?.software_name ?? reportData?.software_name ?? row.company;
  const privacyUrl    = asset?.privacy_url ?? reportData?.privacy_url ?? row.privacyUrl ?? row.privacy_url ?? "";
  const reviewDate    = reportJson?.report_metadata?.review_date  ?? analyzedDate;
  const avgConfidence = (() => {
    const vals = orderedCategoryMeta(categories).map(({ key }) => categories[key]?.confidence_score).filter((v) => v != null);
    return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
  })();
  const reportCategories = orderedCategoryMeta(categories).map(({ key, label }) => ({
    name:        label,
    score:       categories[key]?.score       ?? 0,
    maxScore:    10,
    confidence:  categories[key]?.confidence_score ?? 0,
    verdict:     categories[key]?.indicator   ?? "Neutral",
    description: categories[key]?.summary     ?? "",
  }));

  const pdfResult = !reportLoading && reportCategories.length > 0 ? {
    reviewedAt: reviewDate,
    policyAsOf: report?.policy_date_raw ?? reportJson?.policy_date ?? null,
    overallScore,
    categoriesAnalyzed: reportCategories.length,
    avgConfidence,
    toolSummary,
    executiveSummary: execSummary,
    categories: reportCategories,
  } : null;

  const handleExportPdf = async () => {
    if (!printRef.current || !pdfResult) return;
    setPdfLoading(true);
    try {
      await downloadReportPdf(printRef.current, softwareName);
    } finally {
      setPdfLoading(false);
    }
  };

  const innerContent = (
    <div className="space-y-3">
      <div className="border border-surface-border rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-1.5 text-success text-sm font-semibold">
            <CheckCircle2 className="w-4 h-4" /> Analysis Complete
          </div>
          <h3 className="text-2xl font-extrabold text-brand-text mt-0.5">{softwareName}</h3>
          {privacyUrl && (
            <p className="text-xs text-primary mt-1 break-all">{privacyUrl}</p>
          )}
          <p className="text-xs text-brand-light mt-0.5">Analyzed on {analyzedDate}</p>
          {policyAsOf && (
            <p className="text-xs text-brand-light mt-0.5">Policy As Of {policyAsOf}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportPdf}
            disabled={pdfLoading || !pdfResult}
            className="h-9 px-4 rounded-lg bg-primary text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {pdfLoading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Download className="w-3.5 h-3.5" />
            )}
            {pdfLoading ? "Generating…" : "Export PDF"}
          </button>
        </div>
      </div>

      {/* Archived source policy — the page the agent's report was based on.
          Shared with PolicyResultModal so the two views can't drift. */}
      <SourcePolicyCard assetId={row.assetId} softwareName={softwareName} />

      {reportLoading && (
        <div className="flex justify-center py-10">
          <Loader2 className="w-6 h-6 text-primary animate-spin" />
        </div>
      )}

      {reportError && !reportLoading && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <AlertCircle className="w-4 h-4 shrink-0" /> {reportError}
        </div>
      )}

      {!reportLoading && (
      <div className="border border-surface-border rounded-xl overflow-hidden">
        <div className="flex items-center gap-5 border-b border-surface-border px-4">
          <button
            onClick={() => setTab("summary")}
            className={`h-10 text-sm font-semibold border-b-2 ${tab === "summary" ? "border-primary text-primary" : "border-transparent text-brand-muted"}`}
          >
            Summary
          </button>
          <button
            onClick={() => setTab("report")}
            className={`h-10 text-sm font-semibold border-b-2 ${tab === "report" ? "border-primary text-primary" : "border-transparent text-brand-muted"}`}
          >
            View Report
          </button>
        </div>

        {tab === "summary" ? (
          <div className="p-4 md:p-5 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center">
            <div className="space-y-3">
              <p className="font-bold text-brand-text">Policy Overview</p>
              {orderedCategoryMeta(categories).map(({ key, label, tone }) => {
                const raw = categories[key]?.score ?? null;
                const indicator = categories[key]?.indicator ?? null;
                const display = raw !== null ? `${raw}/10` : "—";
                return (
                  <div key={key} className="flex items-center justify-between text-sm border-b border-surface-border/60 pb-2">
                    <span className="text-brand-muted">{label}</span>
                    <div className="flex items-center gap-2">
                      {indicator && (
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                          indicator === "Good" ? "bg-success/15 text-success" :
                          indicator === "Bad"  ? "bg-danger/15 text-danger"   :
                                                 "bg-warning/15 text-warning"
                        }`}>{indicator}</span>
                      )}
                      <span className={`font-bold ${raw !== null ? tone : "text-brand-light"}`}>{display}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col items-center text-center">
              <div className="relative w-36 h-36">
                <svg className="w-36 h-36 -rotate-90" viewBox="0 0 100 100" aria-hidden>
                  <circle cx="50" cy="50" r="40" stroke="#E2E8F0" strokeWidth="6" fill="none" />
                  <circle cx="50" cy="50" r="40" stroke="#3B5BDB" strokeWidth="6" fill="none" strokeDasharray="251" strokeDashoffset={scoreToOffset(overallScore)} strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <p className="text-5xl font-extrabold text-primary leading-none">{overallScore ?? "—"}</p>
                  <p className="text-xs text-brand-light">Overall Score</p>
                </div>
              </div>
              <p className="text-xs text-brand-light max-w-[180px] mt-2">
                Composite rating across data sharing, usage, retention, security & confidentiality
              </p>
            </div>
          </div>
        ) : (
          <div className="p-5 sm:p-6">
            <div className="rounded-xl border border-surface-border bg-surface/40 p-5 max-h-[420px] overflow-y-auto">
              <div className="bg-white border border-surface-border rounded-xl p-5 space-y-4">
                {/* Report header */}
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <h3 className="text-2xl font-extrabold text-brand-text">{softwareName}</h3>
                    {privacyUrl && (
                      <p className="text-xs text-primary break-all">{privacyUrl}</p>
                    )}
                    <p className="text-sm text-brand-muted">Reviewed on {reviewDate}</p>
                    {policyAsOf && (
                      <p className="text-sm text-brand-muted">Policy As Of {policyAsOf}</p>
                    )}
                  </div>
                  <span className="text-xs font-semibold bg-primary/10 text-primary px-3 py-1 rounded-full">PRIVACY ANALYSIS</span>
                </div>

                {/* Score banner */}
                <div className="border border-indigo-100 bg-indigo-50/40 rounded-xl p-4 flex items-center gap-4 flex-wrap">
                  <div className="w-20 h-20 rounded-full border-4 border-primary/20 border-t-primary flex items-center justify-center text-3xl font-bold text-brand-text">
                    {overallScore}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-brand-muted font-semibold uppercase">Overall privacy score</p>
                    <p className="text-sm text-brand-muted mt-1">
                      Composite rating across data sharing, usage, retention, security, and confidentiality.
                    </p>
                  </div>
                  <div className="text-right min-w-[140px]">
                    <p className="text-xs text-brand-light uppercase">Categories Analysed</p>
                    <p className="text-3xl font-bold text-primary">{reportCategories.length}</p>
                    {avgConfidence !== null && (
                      <>
                        <p className="text-xs text-brand-light uppercase mt-2">Avg Confidence</p>
                        <p className="text-3xl font-bold text-primary">{avgConfidence}%</p>
                      </>
                    )}
                  </div>
                </div>

                {/* Summaries */}
                <div>
                  <h4 className="text-lg font-semibold text-brand-text mb-1">Summaries</h4>
                  <p className="text-xs text-brand-muted mb-3">High-level context for the privacy and data handling posture.</p>
                  <h5 className="text-sm font-semibold text-brand-text mb-1">Tool summary</h5>
                  <p className="text-sm text-brand-muted leading-relaxed">{toolSummary || "—"}</p>
                </div>
                <div>
                  <h5 className="text-sm font-semibold text-brand-text mb-1">Executive summary</h5>
                  <p className="text-sm text-brand-muted leading-relaxed">{execSummary || "—"}</p>
                </div>

                {/* Category cards */}
                <div className="space-y-3 pt-1">
                  {reportCategories.map((cat) => (
                    <HistCatCard key={cat.name} cat={cat} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );

  return (
    <>
      {pdfResult && <ReportPrintView ref={printRef} result={pdfResult} softwareName={softwareName} privacyUrl={privacyUrl} />}
      <Overlay>
      <div className={`w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-white border border-surface-border rounded-xl shadow-2xl p-4 md:p-6`}>
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-3xl font-bold text-primary">Analyze History</h2>
          <button onClick={onClose} className="text-orange-500 hover:text-orange-600">
            <CircleX className="w-5 h-5" />
          </button>
        </div>

        {showSidebar ? (
          <div className="grid grid-cols-1 md:grid-cols-[110px_1fr] gap-3">
            <div className="space-y-2">
              {row.versions.map((v) => (
                <button
                  key={v.key}
                  onClick={() => setActiveVersion(v.key)}
                  className={`w-full rounded-lg p-2 text-left border transition-colors ${
                    activeVersion === v.key ? "bg-primary/5 border-primary/20" : "border-surface-border hover:bg-surface"
                  }`}
                >
                  <p className="text-xs font-semibold text-primary">{v.label}</p>
                  <p className="text-[10px] text-brand-light mt-0.5">Analyzed on {v.date}</p>
                </button>
              ))}
            </div>
            {innerContent}
          </div>
        ) : (
          innerContent
        )}
      </div>
      </Overlay>
    </>
  );
};

const CompareResultModal = ({ data, onClose }) => {
  if (!data) return null;

  const overallFrom = data.fromOverallScore;
  const overallTo = data.toOverallScore;
  const delta = data.overallDelta == null ? null : +Number(data.overallDelta).toFixed(1);
  const shiftRows = Array.isArray(data.categoryDeltas) ? data.categoryDeltas.slice(0, 5) : [];
  const deltaText = delta == null ? "—" : `${delta > 0 ? "+" : ""}${delta.toFixed(1)}`;
  const rangeText = overallFrom == null || overallTo == null ? "From — to —" : `From ${overallFrom} to ${overallTo}`;

  return (
    <Overlay>
      <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-xl border border-surface-border p-5 md:p-6 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-primary">Analysis Compare</h2>
            <p className="text-sm text-brand-muted mt-0.5">Analyze a policy and compare it against others</p>
          </div>
          <button onClick={onClose} className="text-orange-500 hover:text-orange-600">
            <CircleX className="w-5 h-5" />
          </button>
        </div>

        <div className="border border-surface-border rounded-xl p-4 mt-4">
          <div className="flex items-center gap-2 text-success text-sm font-semibold mb-1">
            <CheckCircle2 className="w-4 h-4" /> Comparison Complete
          </div>
          <h3 className="text-2xl font-extrabold text-brand-text">{data.company}</h3>
          <p className="text-xs text-brand-muted mt-0.5">
            {data.v1?.toUpperCase()} ({data.v1Date}) - {data.v2?.toUpperCase()} ({data.v2Date})
          </p>
        </div>

        <div className="border border-surface-border rounded-xl mt-4 overflow-hidden">
          <div className="h-10 border-b border-surface-border px-4 flex items-center text-sm font-semibold text-primary">Summary</div>
          <div className="p-4 md:p-5 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center">
            <div className="space-y-3">
              <p className="font-bold text-brand-text">Largest Category Shift</p>
              {shiftRows.length === 0 ? (
                <p className="text-sm text-brand-muted">No category comparison data available.</p>
              ) : (
                shiftRows.map((row) => (
                  <div key={row.name} className="flex items-center justify-between text-sm">
                    <span className="text-brand-muted">{row.label}</span>
                    <span className="font-semibold text-brand-text">{row.fromScore ?? "—"} to {row.toScore ?? "—"}</span>
                  </div>
                ))
              )}
            </div>

            <div className="flex flex-col items-center text-center">
              <div className="relative w-28 h-28">
                <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100" aria-hidden>
                  <circle cx="50" cy="50" r="40" stroke="#E2E8F0" strokeWidth="6" fill="none" />
                  <circle cx="50" cy="50" r="40" stroke="#3B5BDB" strokeWidth="6" fill="none" strokeDasharray="251" strokeDashoffset={scoreToOffset(overallTo ?? 0)} strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <p className="text-4xl font-extrabold text-primary leading-none">{deltaText}</p>
                  <p className="text-[10px] text-brand-light">Overall Changes</p>
                </div>
              </div>
              <p className="text-xs text-brand-light">{rangeText}</p>
            </div>
          </div>

          <div className="p-4 md:p-5 grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-surface-border">
            <HistCategoryCard
              title={`${data.v1?.toUpperCase()} Summary`}
              text={data.fromExecutiveSummary || "—"}
            />
            <HistCategoryCard
              title={`${data.v2?.toUpperCase()} Summary`}
              text={data.toExecutiveSummary || "—"}
            />
          </div>
        </div>

        {/* ── Document Changes (text diff of the uploaded policy files) ── */}
        {data.documentDiff && (
          <div className="border border-surface-border rounded-xl mt-4 overflow-hidden">
            <div className="h-10 border-b border-surface-border px-4 flex items-center text-sm font-semibold text-primary">
              Document Changes
            </div>
            <div className="p-4 md:p-5">
              {(!data.documentDiff.available || !data.documentDiff.diffable) ? (
                <p className="text-sm text-brand-muted">
                  {data.documentDiff.reason || "Document text is not available to compare."}
                </p>
              ) : (data.documentDiff.hunks?.length ?? 0) === 0
                  || !data.documentDiff.hunks.some((h) => h.added || h.removed) ? (
                <p className="text-sm text-brand-muted">No textual differences between these document versions.</p>
              ) : (
                <>
                  {data.documentDiff.stats && (
                    <p className="text-xs mb-2">
                      <span className="text-success font-semibold">+{data.documentDiff.stats.added_lines} added</span>
                      {"   "}
                      <span className="text-danger font-semibold">−{data.documentDiff.stats.removed_lines} removed</span>
                    </p>
                  )}
                  <div className="max-h-72 overflow-auto rounded-lg bg-gray-50 border border-surface-border p-3 text-[11px] leading-relaxed font-mono whitespace-pre-wrap">
                    {data.documentDiff.hunks.map((h, i) => (
                      <span
                        key={i}
                        className={
                          h.added
                            ? "block bg-green-100 text-green-800"
                            : h.removed
                              ? "block bg-red-100 text-red-800"
                              : "block text-brand-muted"
                        }
                      >
                        {h.value}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </Overlay>
  );
};

// Renders an uploaded policy document as a download link (or "—" when absent).
const PolicyFileLink = ({ assetId, document, sourceSnapshot, softwareName }) => {
  // Prefer the file the user uploaded — it is what they chose to analyse.
  if (document && assetId) {
    return (
      <button
        onClick={() => downloadPolicyDocument(assetId, document.id, document.fileName)}
        className="inline-flex items-center gap-1 text-xs text-primary hover:underline max-w-[200px]"
        title={`Download ${document.fileName} (V${document.version})`}
      >
        <Download className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate">{document.fileName}</span>
        <span className="text-[10px] text-brand-muted shrink-0">V{document.version}</span>
      </button>
    );
  }

  // Otherwise the PDF we captured of the vendor's page during analysis. For a
  // URL-only analysis this IS the policy file, so the column should show it
  // rather than an em dash.
  if (sourceSnapshot && assetId) {
    const slug = String(softwareName || "policy").toLowerCase().replace(/\s+/g, "-");
    return (
      <button
        onClick={() => openAssetSnapshotPdf(assetId, `${slug}-source-policy-v${sourceSnapshot.version}.pdf`)}
        className="inline-flex items-center gap-1 text-xs text-primary hover:underline max-w-[220px]"
        title={`Open the archived source page (${sourceSnapshot.pdf_page_count} pages, captured ${sourceSnapshot.captured_at ? new Date(sourceSnapshot.captured_at).toLocaleDateString() : ""})`}
      >
        <FileText className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate">Source policy</span>
        <span className="text-[10px] text-brand-muted shrink-0">{sourceSnapshot.pdf_page_count}pp</span>
      </button>
    );
  }

  return <span className="text-brand-muted text-xs">—</span>;
};


// ── Compare two of a vendor's policy documents ───────────────────────────────
// Distinct from the version compare above, which diffs two runs of ONE document.
// This diffs two DIFFERENT documents of the same vendor — Dropbox's policy in
// force against the one they have already published for 2027. It reads archived
// snapshots, so there is no new analysis and nothing to wait for.
const CompareDocumentsModal = ({ vendorId, vendorName, onClose }) => {
  const [options, setOptions] = useState(null);
  const [fromId, setFromId]   = useState("");
  const [toId, setToId]       = useState("");
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState("");

  useEffect(() => {
    if (!vendorId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    getVendorComparisonOptions(vendorId)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data ?? {};
        setOptions(data);
        // Default to the most useful pairing: what is in force now against
        // whatever comes next.
        const docs = (data.documents ?? []).filter((d) => d.snapshots?.length);
        const current = docs.find((d) => d.policy_role === "current") ?? docs[0];
        const other   = docs.find((d) => d !== current);
        if (current?.snapshots?.[0]) setFromId(String(current.snapshots[0].id));
        if (other?.snapshots?.[0])   setToId(String(other.snapshots[0].id));
      })
      .catch((err) => { if (!cancelled) setError(err?.message || "Couldn't load this vendor's documents."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [vendorId]);

  const run = async () => {
    if (!fromId || !toId || fromId === toId) {
      setError("Pick two different documents to compare.");
      return;
    }
    setBusy(true); setError(""); setResult(null);
    try {
      const res = await compareSnapshots(fromId, toId);
      setResult(res?.data ?? null);
    } catch (err) {
      setError(err?.message || "Comparison failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!vendorId) return null;

  const docs = options?.documents ?? [];
  const archived = docs.filter((d) => d.snapshots?.length);

  const picker = (value, setValue, label) => (
    <div className="flex flex-col gap-1.5 flex-1 min-w-[240px]">
      <span className="text-xs font-semibold text-brand-muted">{label}</span>
      <select
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="border border-surface-border rounded-lg px-3 py-2 text-sm bg-white text-brand-text"
      >
        <option value="">Select a document…</option>
        {archived.map((d) => d.snapshots.map((s) => (
          <option key={s.id} value={s.id}>
            {(d.policy_role || "policy")} · v{s.snapshot_version}
            {d.declared_date ? ` · ${d.declared_date_kind || "dated"} ${d.declared_date}` : ""}
          </option>
        )))}
      </select>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-4xl my-8 shadow-xl">
        <div className="flex items-start justify-between gap-4 p-5 border-b border-surface-border">
          <div>
            <h3 className="text-lg font-bold text-brand-text">Compare {vendorName} policies</h3>
            <p className="text-sm text-brand-muted mt-0.5">
              Two different documents from this vendor, read from the archived copies.
            </p>
          </div>
          <button onClick={onClose} className="text-brand-muted hover:text-brand-text"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 flex flex-col gap-4">
          {loading && (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
          )}

          {!loading && archived.length < 2 && (
            <div className="text-sm text-brand-muted bg-surface border border-surface-border rounded-xl p-4">
              This vendor needs at least two archived policy documents to compare.
              {docs.length > archived.length && " Some of its documents have no archived copy yet — re-run the analysis to capture one."}
            </div>
          )}

          {!loading && archived.length >= 2 && (
            <>
              <div className="flex flex-wrap items-end gap-3">
                {picker(fromId, setFromId, "From")}
                {picker(toId, setToId, "To")}
                <button
                  onClick={run}
                  disabled={busy}
                  className="inline-flex items-center gap-2 bg-primary text-white rounded-lg px-5 py-2.5 text-sm font-semibold disabled:opacity-60"
                >
                  {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Comparing…</> : <><GitCompareArrows className="w-4 h-4" /> Compare</>}
                </button>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {error}
                </div>
              )}

              {result && <ComparisonResult result={result} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/** The rendered comparison: how it is framed, what the scores did, what changed. */
const ComparisonResult = ({ result }) => {
  const { comparison_type: type, from, to, scores, text } = result;
  const isForecast = type === "forecast";
  const moved = (scores?.categories ?? []).filter((c) => c.delta);

  return (
    <div className="flex flex-col gap-4">
      {/* Framing: a policy not yet in force is a forecast, not history. */}
      <div className={`rounded-xl border px-4 py-3 ${isForecast ? "bg-yellow-50 border-yellow-200" : "bg-surface border-surface-border"}`}>
        <p className="text-sm font-semibold text-brand-text">
          {isForecast ? "What changes when the new policy takes effect" : "What changed between these versions"}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2.5 text-xs">
          {[from, to].map((side, i) => (
            <div key={side.snapshot_id} className="flex flex-col gap-0.5">
              <span className="font-semibold text-brand-muted uppercase tracking-wide">{i === 0 ? "From" : "To"}</span>
              <span className="text-brand-text font-medium capitalize">{side.policy_role || "policy"}</span>
              <span className="text-brand-muted break-all">{side.url}</span>
              {side.declared_date && (
                <span className="text-brand-muted">{side.declared_date_kind} {side.declared_date}</span>
              )}
              <a href={side.pdf_download_url} className="text-primary font-semibold mt-0.5">
                Source PDF · {side.pdf_pages} pages
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* Scores */}
      <div className="border border-surface-border rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 border-b border-surface-border flex items-baseline justify-between">
          <span className="text-sm font-semibold text-brand-text">Score movement</span>
          <span className="text-sm text-brand-muted">
            Overall {scores?.overall_before ?? "—"} → <strong className="text-brand-text">{scores?.overall_after ?? "—"}</strong>
          </span>
        </div>
        {moved.length === 0 ? (
          <p className="px-4 py-3 text-sm text-brand-muted">No category score moved.</p>
        ) : moved.map((c) => (
          <div key={c.name} className="flex items-center justify-between px-4 py-2.5 border-b border-surface-border last:border-0 text-sm">
            <span className="text-brand-text">{c.name}</span>
            <span className={`font-bold ${c.delta < 0 ? "text-danger" : "text-success"}`}>
              {c.before} → {c.after} ({c.delta > 0 ? "+" : ""}{c.delta})
            </span>
          </div>
        ))}
      </div>

      {/* Text diff */}
      <div className="border border-surface-border rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 border-b border-surface-border flex items-baseline justify-between gap-3 flex-wrap">
          <span className="text-sm font-semibold text-brand-text">What changed in the policy text</span>
          {text?.stats && (
            <span className="text-xs text-brand-muted">
              {text.stats.sentences_added} added · {text.stats.sentences_removed} removed
            </span>
          )}
        </div>
        {!text?.diffable ? (
          <p className="px-4 py-3 text-sm text-brand-muted">{text?.reason || "No text available to compare."}</p>
        ) : (
          <div className="max-h-[420px] overflow-y-auto text-xs leading-relaxed">
            {text.hunks.slice(0, 80).map((h, i) => (
              <div
                key={i}
                className={`px-4 py-1.5 border-l-4 ${h.added ? "bg-green-50 border-green-500" : "bg-red-50 border-red-400"}`}
              >
                <span className={`font-bold mr-2 ${h.added ? "text-green-700" : "text-red-600"}`}>{h.added ? "+" : "−"}</span>
                <span className="text-brand-text">{h.value.replace(/\s+/g, " ").trim().slice(0, 400)}</span>
              </div>
            ))}
            {text.hunks.length > 80 && (
              <p className="px-4 py-2 text-brand-muted">…and {text.hunks.length - 80} more changes.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const CompletedRow = ({ row, onView, onPdf, onCompare, onDelete, onCompareDocuments }) => {
  const [open, setOpen] = useState(false);
  const hasMultipleVersions = (row.versions?.length ?? 0) > 1;

  return (
    <>
      <tr className="border-b border-surface-border hover:bg-surface/30 transition-colors">
        <td className="px-3 py-3.5">
          <button
            onClick={() => setOpen((v) => !v)}
            className="text-brand-muted hover:text-brand-text transition-colors"
          >
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </td>
        <td className="px-4 py-3.5">
          <div className="flex items-center gap-2">
            <span className="font-medium text-brand-text">{row.company}</span>
            {/* Which document of this vendor. Only shown when it disambiguates —
                labelling a lone ordinary policy "current" is noise. */}
            {row.policyRole && row.policyRole !== "current" && (
              <span className="text-[10px] font-semibold bg-primary/10 text-primary px-2 py-0.5 rounded-full capitalize">
                {row.policyRole}
              </span>
            )}
            {row.isVendorFirstRow && row.vendorDocumentCount > 1 && (
              <span className="text-[10px] font-semibold bg-surface-border text-brand-muted px-2 py-0.5 rounded-full">
                {row.vendorDocumentCount} documents
              </span>
            )}
            {hasMultipleVersions && (
              <span className="text-[10px] font-semibold bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                {row.versions.length} versions
              </span>
            )}
            {row.policyChanged && (
              <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-500 rounded-full px-2 py-0.5 text-[10px] font-semibold">
                <AlertCircle className="w-2.5 h-2.5" /> Policy Changed
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-3.5">
          {row.privacyUrl ? (
            <span className="text-brand-muted text-xs break-all">{row.privacyUrl}</span>
          ) : (
            <span className="text-brand-muted text-xs">—</span>
          )}
        </td>
        <td className="px-4 py-3.5">
          <PolicyFileLink assetId={row.assetId} document={row.document} sourceSnapshot={row.sourceSnapshot} softwareName={row.company} />
        </td>
        <td className="px-4 py-3.5 text-brand-muted">{row.date}</td>
        <td className="px-4 py-3.5 text-brand-muted whitespace-nowrap">
          {row.cached && row.runtimeMs == null
            ? <span className="text-xs" title="Reused an existing analysis of this URL — no new run">Cached</span>
            : formatDuration(row.runtimeMs)}
        </td>
        <td className="px-4 py-3.5">
          <span className="inline-block text-[10px] bg-success/15 text-success px-2.5 py-0.5 rounded-full font-semibold">Complete</span>
        </td>
        <td className="px-4 py-3.5 text-right">
          <div className="inline-flex items-center gap-3">
            <button onClick={() => onView(row)} className="inline-flex items-center gap-1 text-primary text-xs font-medium hover:opacity-80">
              <Eye className="w-3.5 h-3.5" /> View
            </button>
            <button onClick={() => onDelete(row)} title="Delete analysis" className="inline-flex items-center gap-1 text-brand-muted text-xs font-medium hover:text-danger">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </td>
      </tr>

      {open && (
        <>
          <tr className="bg-gray-100 border-b border-surface-border">
            <td className="px-3" />
            <td className="px-4 py-2 text-[10px] uppercase font-semibold text-gray-500">Version</td>
            <td />
            <td className="px-4 py-2 text-[10px] uppercase font-semibold text-gray-500">Policy File</td>
            <td className="px-4 py-2 text-[10px] uppercase font-semibold text-gray-500">Date & Time</td>
            <td className="px-4 py-2 text-[10px] uppercase font-semibold text-gray-500">Run Time</td>
            <td />
            <td className="px-4 py-2 text-[10px] uppercase font-semibold text-gray-500 text-left">Actions</td>
          </tr>
          {row.versions.map((ver) => (
            <tr key={ver.key} className="bg-surface/10 border-b border-surface-border last:border-0">
              <td className="px-3 py-2" />
              <td className="px-4 py-2">
                <div className="flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-full text-white text-[10px] font-bold inline-flex items-center justify-center ${ver.latest ? "bg-primary" : "bg-brand-light"}`}>
                    {ver.label.toLowerCase()}
                  </span>
                  {ver.latest && <span className="text-[10px] text-primary font-semibold">Latest</span>}
                </div>
              </td>
              <td />
              <td className="px-4 py-2"><PolicyFileLink assetId={row.assetId} document={ver.document} sourceSnapshot={row.sourceSnapshot} softwareName={row.company} /></td>
              <td className="px-4 py-2 text-xs text-brand-muted">{ver.date}</td>
              <td className="px-4 py-2 text-xs text-brand-muted whitespace-nowrap">{formatDuration(ver.runtimeMs)}</td>
              <td />
              <td className="px-4 py-2">
                <div className="flex items-center gap-3 text-xs">
                  <button onClick={() => onView(row, ver)} className="inline-flex items-center gap-1 text-primary"><Eye className="w-3.5 h-3.5" /> View</button>
                  <button onClick={() => onPdf(row, ver)} className="inline-flex items-center gap-1 text-brand-muted"><Download className="w-3.5 h-3.5" /> PDF</button>
                  {hasMultipleVersions && (
                    <button onClick={() => onCompare(row)} className="inline-flex items-center gap-1 text-orange-500"><GitCompareArrows className="w-3.5 h-3.5" /> Compare</button>
                  )}
                  {/* Compare two DIFFERENT documents of this vendor — e.g. the
                      policy in force against a forthcoming one. */}
                  {row.isVendorFirstRow && row.vendorDocumentCount > 1 && onCompareDocuments && (
                    <button
                      onClick={() => onCompareDocuments(row)}
                      className="inline-flex items-center gap-1 text-primary font-semibold"
                    >
                      <GitCompareArrows className="w-3.5 h-3.5" /> Compare documents
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </>
      )}
    </>
  );
};

const CompletedTable = ({ data, loading, page, totalPages, totalCount, onPage, onView, onPdf, onCompare, onDelete, onCompareDocuments }) => (
  <div className="bg-white border border-surface-border rounded-2xl overflow-hidden">
    <div className="overflow-x-auto">
    <table className="w-full text-sm min-w-[860px]">
      <thead>
        <tr className="border-b border-surface-border">
          <th className="w-8 px-3 py-3" />
          <th className="text-left px-4 py-3 text-xs text-brand-muted font-medium">Software Name</th>
          <th className="text-left px-4 py-3 text-xs text-brand-muted font-medium">Privacy URL</th>
          <th className="text-left px-4 py-3 text-xs text-brand-muted font-medium">Policy File</th>
          <th className="text-left px-4 py-3 text-xs text-brand-muted font-medium">Date & Time</th>
          <th className="text-left px-4 py-3 text-xs text-brand-muted font-medium">Run Time</th>
          <th className="text-left px-4 py-3 text-xs text-brand-muted font-medium">Status</th>
          <th className="text-right px-4 py-3 text-xs text-brand-muted font-medium">Actions</th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr><td colSpan={8} className="py-12 text-center"><Loader2 className="w-5 h-5 text-primary animate-spin inline-block" /></td></tr>
        ) : data.length === 0 ? (
          <tr><td colSpan={8} className="py-12 text-center text-sm text-brand-muted">No results found.</td></tr>
        ) : (
          data.map((row) => (
            <CompletedRow key={row.id} row={row} onView={onView} onPdf={onPdf} onCompare={onCompare} onDelete={onDelete} onCompareDocuments={onCompareDocuments} />
          ))
        )}
      </tbody>
    </table>
    </div>

    <div className="flex items-center justify-between px-4 py-3 border-t border-surface-border">
      <p className="text-[11px] text-brand-light">
        Showing {data.length === 0 ? 0 : (page - 1) * PAGE_LIMIT + 1}-{Math.min(page * PAGE_LIMIT, totalCount)} of {totalCount} results
      </p>
      <div className="flex items-center gap-2">
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
          <button
            key={p}
            onClick={() => onPage(p)}
            className={`w-6 h-6 rounded text-xs ${p === page ? "bg-primary text-white" : "text-brand-muted"}`}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  </div>
);

const OngoingList = ({ data, loading, onCancel, onDelete }) => (
  <div className="bg-white border border-surface-border rounded-2xl overflow-hidden">
    <div className="border-b border-surface-border px-4 py-3 flex items-center gap-2">
      <span className="w-2 h-2 rounded-full bg-success" />
      <p className="text-sm font-semibold text-brand-text">Ongoing Analysis ({data.length})</p>
      <p className="text-[11px] text-brand-light">Running in real-time</p>
    </div>

    {loading ? (
      <div className="py-10 flex justify-center">
        <Loader2 className="w-5 h-5 text-primary animate-spin" />
      </div>
    ) : data.length === 0 ? (
      <p className="py-10 text-center text-sm text-brand-muted">No ongoing analyses.</p>
    ) : data.map((row, idx) => (
      <div
        key={row.id}
        className={`grid grid-cols-[40px_1fr_auto_auto] items-center gap-3 px-4 py-3 ${
          idx < data.length - 1 ? "border-b border-surface-border" : ""
        }`}
      >
        <div className="w-7 h-7 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        </div>
        <div>
          <p className="text-sm font-semibold text-brand-text">{row.company}</p>
          <p className="text-[10px] text-primary font-medium">Analysis · v{row.version}</p>
          {row.privacyUrl && (
            <p className="text-[10px] text-brand-light truncate max-w-xs">{row.privacyUrl}</p>
          )}
        </div>
        <span className="text-[11px] text-success inline-flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-success" /> Processing
        </span>
        <div className="inline-flex items-center gap-3">
          <button onClick={() => onCancel(row)} title="Cancel analysis" className="text-orange-500 hover:text-orange-600">
            <CircleX className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(row)} title="Delete analysis" className="text-brand-muted hover:text-danger">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    ))}
  </div>
);

const FailedTable = ({ data, loading, onReAnalyze, onDelete }) => (
  <div className="bg-white border border-surface-border rounded-2xl overflow-hidden">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-surface-border">
          <th className="text-left px-4 py-3 text-xs text-brand-muted font-medium">Software Name</th>
          <th className="text-left px-4 py-3 text-xs text-brand-muted font-medium">Privacy URL</th>
          <th className="text-left px-4 py-3 text-xs text-brand-muted font-medium">Date</th>
          <th className="text-left px-4 py-3 text-xs text-brand-muted font-medium">Status</th>
          <th className="text-right px-4 py-3 text-xs text-brand-muted font-medium">Actions</th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr><td colSpan={5} className="py-12 text-center"><Loader2 className="w-5 h-5 text-primary animate-spin inline-block" /></td></tr>
        ) : data.length === 0 ? (
          <tr><td colSpan={5} className="py-12 text-center text-sm text-brand-muted">No failed analyses.</td></tr>
        ) : data.map((row) => (
          <tr key={row.id} className="border-b border-surface-border last:border-0">
            <td className="px-4 py-3.5 font-medium text-brand-text">{row.company}</td>
            <td className="px-4 py-3.5">
              {row.privacyUrl ? (
                <span className="text-brand-muted text-xs break-all">{row.privacyUrl}</span>
              ) : (
                <span className="text-brand-muted text-xs">—</span>
              )}
            </td>
            <td className="px-4 py-3.5 text-brand-muted">{row.date}</td>
            <td className="px-4 py-3.5">
              <span className="inline-block bg-danger/10 text-danger text-[10px] font-semibold px-2.5 py-0.5 rounded-full">Failed</span>
            </td>
            <td className="px-4 py-3.5 text-right">
              <div className="inline-flex items-center gap-3">
                <button onClick={() => onReAnalyze(row)} className="inline-flex items-center gap-1 text-brand-muted text-xs font-medium hover:text-brand-text">
                  <RotateCcw className="w-3.5 h-3.5" /> Re-Analyze
                </button>
                <button onClick={() => onDelete(row)} title="Delete analysis" className="inline-flex items-center gap-1 text-brand-muted text-xs font-medium hover:text-danger">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const HistoryPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Completed");
  const [searchInput, setSearchInput] = useState("");   // what the user is typing
  const [searchQuery, setSearchQuery] = useState("");   // committed value sent to API
  const [page, setPage]           = useState(1);

  // API data
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [completedCount, setCompletedCount] = useState(0);
  const [processingCount, setProcessingCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [totalAssets, setTotalAssets] = useState(0);

  // Modals
  const [resultsModal,   setResultsModal]   = useState(null);
  const [compareModal,   setCompareModal]   = useState(null);
  const [docCompare,     setDocCompare]     = useState(null);
  const [compareResult,  setCompareResult]  = useState(null);
  const [compareBusy,    setCompareBusy]    = useState(false);
  const [compareError,   setCompareError]   = useState("");
  const [cancelModal,    setCancelModal]    = useState(null);
  const [reAnalyzeModal, setReAnalyzeModal] = useState(null);
  const [deleteModal,    setDeleteModal]    = useState(null);
  const [deleteBusy,     setDeleteBusy]     = useState(false);
  const [thankYouOpen,   setThankYouOpen]   = useState(false);
  const [thankYouText,   setThankYouText]   = useState("Your action was completed successfully.");

  // Direct PDF download (without opening the modal)
  const [directPdf,     setDirectPdf]     = useState(null); // { pdfResult, softwareName }
  const [directPdfBusy, setDirectPdfBusy] = useState(false);
  const directPdfRef = useRef(null);

  useEffect(() => {
    if (!directPdf || !directPdfRef.current) return;
    let cancelled = false;
    downloadReportPdf(directPdfRef.current, directPdf.softwareName)
      .finally(() => {
        if (!cancelled) {
          setDirectPdf(null);
          setDirectPdfBusy(false);
        }
      });
    return () => { cancelled = true; };
  }, [directPdf]);

  const handleDirectPdf = useCallback(async (row, ver) => {
    const revisionId = ver?.revisionId ?? row?.revisionId;
    if (!revisionId || directPdfBusy) return;
    setDirectPdfBusy(true);
    try {
      const res = await getRevisionReport(revisionId);
      const reportData   = res?.data ?? res ?? null;
      const asset        = reportData?.asset  ?? null;
      const report       = reportData?.report ?? null;
      const reportJson   = report?.report_json ?? null;
      const categories   = reportJson?.privacy_analysis ?? {};
      const categoryScores = orderedCategoryMeta(categories).map(({ key }) => categories[key]?.score).filter((s) => s != null);
      const overallScore   = categoryScores.length
        ? +(categoryScores.reduce((a, b) => a + b, 0) / categoryScores.length).toFixed(1)
        : (row.overallScore ?? 0);
      const vals = orderedCategoryMeta(categories).map(({ key }) => categories[key]?.confidence_score).filter((v) => v != null);
      const avgConfidence = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
      const reportCategories = orderedCategoryMeta(categories).map(({ key, label }) => ({
        name:        label,
        score:       categories[key]?.score            ?? 0,
        maxScore:    10,
        confidence:  categories[key]?.confidence_score ?? 0,
        verdict:     categories[key]?.indicator        ?? "Neutral",
        description: categories[key]?.summary          ?? "",
      }));
      const softwareName = asset?.software_name ?? reportData?.software_name ?? row.company;
      const analyzedDate = report?.created_at
        ? new Date(report.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
        : row.date;
      const pdfResult = {
        // title:              reportJson?.report_metadata?.report_title ?? "Privacy and Data Handling Review",
        reviewedAt:         reportJson?.report_metadata?.review_date  ?? analyzedDate,
        policyAsOf:         report?.policy_date_raw ?? reportJson?.policy_date ?? null,
        overallScore,
        categoriesAnalyzed: reportCategories.length,
        avgConfidence,
        toolSummary:        reportJson?.website_summary   ?? "",
        executiveSummary:   reportJson?.executive_summary ?? "",
        categories:         reportCategories,
      };
      // Setting directPdf triggers the useEffect above which fires downloadReportPdf
      // once the hidden ReportPrintView has mounted.
      setDirectPdf({ pdfResult, softwareName });
    } catch (err) {
      console.error("PDF download failed:", err);
      setDirectPdfBusy(false);
    }
  }, [directPdfBusy]);

  // ── Fetch history from API ─────────────────────────────────────────
  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getHistory({
        page,
        limit:           PAGE_LIMIT,
        analysis_status: TAB_STATUS[activeTab],
        search:          searchQuery,
      });
      const items = res?.data?.history ?? [];
      const rawRows = items.map(toRow);

      // Group by assetId: multiple analysis runs for the same asset become versions
      const assetMap = new Map();
      rawRows.forEach((row) => {
        const key = row.assetId ?? row.id;
        if (assetMap.has(key)) {
          // Push this run's version into the existing group
          assetMap.get(key).versions.push(...row.versions);
        } else {
          assetMap.set(key, { ...row, versions: [...row.versions] });
        }
      });
      const grouped = Array.from(assetMap.values());

      // Keep a vendor's documents adjacent and ordered, so "Dropbox" reads as
      // one company with two policies rather than two unrelated rows that
      // happen to share a name. Vendors stay in most-recent-activity order.
      const vendorFirstSeen = new Map();
      grouped.forEach((r, i) => {
        const key = r.vendorId ?? `asset-${r.assetId}`;
        if (!vendorFirstSeen.has(key)) vendorFirstSeen.set(key, i);
      });
      const ROLE_ORDER = { current: 0, forthcoming: 1, regional: 2, product: 3, other: 4 };
      grouped.sort((a, b) => {
        const ka = a.vendorId ?? `asset-${a.assetId}`;
        const kb = b.vendorId ?? `asset-${b.assetId}`;
        if (ka !== kb) return vendorFirstSeen.get(ka) - vendorFirstSeen.get(kb);
        return (ROLE_ORDER[a.policyRole] ?? 9) - (ROLE_ORDER[b.policyRole] ?? 9);
      });

      // Mark the first row of each vendor and how many documents it has, so the
      // table can label the group without nesting into a collapsible tree.
      const vendorCounts = new Map();
      grouped.forEach((r) => {
        const key = r.vendorId ?? `asset-${r.assetId}`;
        vendorCounts.set(key, (vendorCounts.get(key) || 0) + 1);
      });
      const seenVendor = new Set();
      grouped.forEach((r) => {
        const key = r.vendorId ?? `asset-${r.assetId}`;
        r.vendorDocumentCount = vendorCounts.get(key);
        r.isVendorFirstRow = !seenVendor.has(key);
        seenVendor.add(key);
      });
      // Sort versions within each group (highest version first) and mark latest
      grouped.forEach((row) => {
        row.versions.sort((a, b) => {
          const na = parseInt(a.key.replace("v", ""), 10) || 0;
          const nb = parseInt(b.key.replace("v", ""), 10) || 0;
          return nb - na;
        });
        row.versions.forEach((v, i) => { v.latest = i === 0; });
        // The collapsed row shows the most recent version that has an uploaded
        // document (a re-analysis without a new upload reuses the prior one).
        row.document = row.versions.find((v) => v.document)?.document ?? row.document ?? null;
        // Collapsed row shows the latest run's duration.
        row.runtimeMs = row.versions[0]?.runtimeMs ?? row.runtimeMs ?? null;
      });

      setRows(grouped);
      setPagination({
        total:      res?.meta?.pagination?.total      ?? items.length,
        totalPages: res?.meta?.pagination?.totalPages ?? 1,
      });
      if (res?.data?.completed  !== undefined) setCompletedCount(res.data.completed);
      if (res?.data?.processing !== undefined) setProcessingCount(res.data.processing);
      if (res?.data?.failed     !== undefined) setFailedCount(res.data.failed);
      if (res?.data?.total_assets !== undefined) setTotalAssets(res.data.total_assets);
    } catch (err) {
      setError(err.message || "Failed to load history.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, searchQuery]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const onTabChange = (tab) => {
    setActiveTab(tab);
    setSearchInput("");
    setSearchQuery("");
    setPage(1);
  };

  // Commit search on Enter — calls API with new query from page 1
  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      setSearchQuery(searchInput.trim());
      setPage(1);
    }
  };

  // Clear search
  const handleSearchClear = () => {
    setSearchInput("");
    setSearchQuery("");
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-4">
      <HistoryResultsModal
        key={resultsModal ? `${resultsModal.row.id}-${resultsModal.ver?.key ?? "main"}` : undefined}
        row={resultsModal?.row ?? null}
        specificVersion={resultsModal?.ver ?? null}
        onClose={() => setResultsModal(null)}
      />
      {docCompare && (
        <CompareDocumentsModal
          vendorId={docCompare.vendorId}
          vendorName={docCompare.vendorName}
          onClose={() => setDocCompare(null)}
        />
      )}
      <CompareSelectModal
        row={compareModal}
        loading={compareBusy}
        error={compareError}
        onClose={() => {
          setCompareModal(null);
          setCompareError("");
        }}
        onCompare={async (v1, v2) => {
          const leftVer = compareModal?.versions?.find((v) => v.key === v1) ?? null;
          const rightVer = compareModal?.versions?.find((v) => v.key === v2) ?? null;
          const fromRevisionId = leftVer?.revisionId;
          const toRevisionId = rightVer?.revisionId;
          const assetId = compareModal?.assetId;

          if (!assetId || !fromRevisionId || !toRevisionId) {
            setCompareError("Selected versions are missing revision details.");
            return;
          }

          setCompareBusy(true);
          setCompareError("");

          try {
            // Run the analysis-report comparison and the uploaded-document text
            // diff in parallel. The doc diff is best-effort — never block the
            // report comparison if it fails or a version has no stored document.
            const fromDocId = leftVer?.document?.id ?? null;
            const toDocId = rightVer?.document?.id ?? null;
            const canDiffDocs = fromDocId && toDocId && fromDocId !== toDocId;

            const [response, docDiffRes] = await Promise.all([
              getRevisionComparison(assetId, fromRevisionId, toRevisionId),
              canDiffDocs
                ? comparePolicyDocuments(assetId, fromDocId, toDocId).catch((e) => ({ __error: e }))
                : Promise.resolve(null),
            ]);

            const { fromJson, toJson } = extractComparePayload(response);
            const fromCategories = getPrivacyCategoryList(fromJson);
            const toCategories = getPrivacyCategoryList(toJson);
            const fromOverallScore = computeOverallFromCategories(fromCategories);
            const toOverallScore = computeOverallFromCategories(toCategories);
            const overallDelta = fromOverallScore !== null && toOverallScore !== null
              ? Number((toOverallScore - fromOverallScore).toFixed(1))
              : null;

            // Shape the document-diff result for the modal.
            let documentDiff;
            if (!fromDocId || !toDocId) {
              const missing = [];
              if (!fromDocId) missing.push(leftVer?.label || v1);
              if (!toDocId) missing.push(rightVer?.label || v2);
              documentDiff = {
                available: false,
                reason: `No uploaded policy document is stored for ${missing.join(" and ")}. `
                  + `Pick two versions that show a filename in the Policy File column to compare documents.`,
              };
            } else if (fromDocId === toDocId) {
              documentDiff = { available: false, reason: "Both selected versions reference the same uploaded document." };
            } else if (docDiffRes?.__error) {
              documentDiff = { available: false, reason: docDiffRes.__error.message || "Document comparison failed." };
            } else {
              const d = docDiffRes?.data ?? {};
              documentDiff = d.diffable
                ? { available: true, diffable: true, hunks: d.hunks ?? [], stats: d.stats ?? null }
                : { available: true, diffable: false, reason: d.reason || "Document text is not available to diff." };
            }

            setCompareModal(null);
            setCompareResult({
              company: compareModal.company,
              v1,
              v2,
              v1Date: leftVer?.date ?? "-",
              v2Date: rightVer?.date ?? "-",
              fromOverallScore,
              toOverallScore,
              overallDelta,
              categoryDeltas: computeCategoryDeltas(fromCategories, toCategories),
              fromExecutiveSummary: extractExecutiveSummary(fromJson),
              toExecutiveSummary: extractExecutiveSummary(toJson),
              documentDiff,
            });
          } catch (err) {
            setCompareError(err?.message || "Comparison failed.");
          } finally {
            setCompareBusy(false);
          }
        }}
      />
      <CompareResultModal data={compareResult} onClose={() => setCompareResult(null)} />
      <CancelAnalysisModal
        row={cancelModal}
        onBack={() => setCancelModal(null)}
        onConfirm={() => {
          setCancelModal(null);
          setThankYouText("Ongoing analysis canceled successfully.");
          setThankYouOpen(true);
        }}
      />
      <ReAnalyzeConfirmModal
        row={reAnalyzeModal}
        onClose={() => setReAnalyzeModal(null)}
        onConfirm={() => {
          const row = reAnalyzeModal;
          setReAnalyzeModal(null);
          if (!row?.assetId) return;
          // AnalyzePage owns the re-analysis flow (progress, result modal, errors);
          // hand it the asset and let it start the run.
          navigate("/analyze", { state: { reanalyse: { assetId: row.assetId, softwareName: row.company } } });
        }}
      />
      <DeleteConfirmModal
        row={deleteModal}
        busy={deleteBusy}
        onClose={() => { if (!deleteBusy) setDeleteModal(null); }}
        onConfirm={async () => {
          if (!deleteModal || deleteBusy) return;
          const assetId = deleteModal.assetId;
          if (!assetId) { setDeleteModal(null); return; }
          setDeleteBusy(true);
          try {
            await deleteAsset(assetId);
            setDeleteModal(null);
            setThankYouText("Analysis deleted.");
            setThankYouOpen(true);
            fetchHistory();
          } catch (err) {
            setDeleteModal(null);
            setThankYouText(err?.message || "Failed to delete the analysis.");
            setThankYouOpen(true);
          } finally {
            setDeleteBusy(false);
          }
        }}
      />
      <ThankYouModal isOpen={thankYouOpen} onClose={() => setThankYouOpen(false)} message={thankYouText} />

      {/* Hidden print surface for direct-row PDF download */}
      {directPdf && (
        <div className="fixed -top-[9999px] -left-[9999px] pointer-events-none" aria-hidden="true">
          <ReportPrintView ref={directPdfRef} result={directPdf.pdfResult} softwareName={directPdf.softwareName} />
        </div>
      )}

      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-brand-text">Analysis History</h1>
        <p className="text-sm text-brand-muted mt-0.5">{totalAssets} total analyses</p>
      </div>

      {/* KPI cards — all per-asset, consistent with the dashboard */}
      <div className="grid grid-cols-3 gap-4 max-w-2xl">
        <div className="bg-white border border-surface-border rounded-2xl p-4 flex items-start justify-between">
          <div>
            <p className="text-4xl font-extrabold text-brand-text">{completedCount}</p>
            <p className="text-sm text-brand-text mt-1">Completed Analyses</p>
          </div>
        </div>
        <div className="bg-white border border-surface-border rounded-2xl p-4 flex items-start justify-between">
          <div>
            <p className="text-4xl font-extrabold text-brand-text">{processingCount}</p>
            <p className="text-sm text-brand-text mt-1">Ongoing Analyses</p>
          </div>
          <div className="w-9 h-9 rounded-xl bg-violet-100 text-violet-500 flex items-center justify-center">
            <Clock className="w-4 h-4" />
          </div>
        </div>
        <div className="bg-white border border-surface-border rounded-2xl p-4 flex items-start justify-between">
          <div>
            <p className="text-4xl font-extrabold text-brand-text">{failedCount}</p>
            <p className="text-sm text-brand-text mt-1">Failed Analyses</p>
          </div>
          <div className="w-9 h-9 rounded-xl bg-danger/10 text-danger flex items-center justify-center">
            <AlertCircle className="w-4 h-4" />
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-64">
          <Search className="w-3.5 h-3.5 text-brand-light absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={searchInput}
            onChange={(e) => {
              const val = e.target.value;
              setSearchInput(val);
              if (val === "") {
                setSearchQuery("");
                setPage(1);
              }
            }}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search by software name"
            className="w-full h-9 pl-8 pr-8 rounded-lg border border-surface-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
          {searchInput && (
            <button
              onClick={handleSearchClear}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-brand-light hover:text-brand-text"
            >
              <CircleX className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-brand-muted">Status:</span>
          <div className="flex items-center gap-1">
            {TABS.map((tab) => (
              <button
                key={tab}
                onClick={() => onTabChange(tab)}
                className={`h-8 px-4 rounded-lg text-xs font-semibold border transition-colors ${
                  activeTab === tab
                    ? "bg-primary text-white border-primary"
                    : "bg-white text-brand-muted border-surface-border hover:text-brand-text"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tables */}
      {activeTab === "Completed" && (
        <CompletedTable
          data={rows}
          loading={loading}
          page={page}
          totalPages={Math.max(1, Math.ceil(completedCount / PAGE_LIMIT))}
          totalCount={completedCount}
          onPage={(p) => setPage(p)}
          onView={(row, ver) => setResultsModal({ row, ver: ver ?? null })}
          onPdf={handleDirectPdf}
          onCompare={setCompareModal}
          onCompareDocuments={(row) => setDocCompare({ vendorId: row.vendorId, vendorName: row.vendorName || row.company })}
          onDelete={setDeleteModal}
        />
      )}

      {activeTab === "Ongoing" && (
        <OngoingList data={rows} loading={loading} onCancel={setCancelModal} onDelete={setDeleteModal} />
      )}

      {activeTab === "Failed" && (
        <FailedTable data={rows} loading={loading} onReAnalyze={setReAnalyzeModal} onDelete={setDeleteModal} />
      )}
    </div>
  );
};

export { HistoryResultsModal };
export default HistoryPage;
